/**
 * Header and mobile menu drawer.
 * Loaded by the header; the guard below keeps it to one run.
 */
(function () {
  if (window.LumiHeaderReady) return;
  window.LumiHeaderReady = true;

  const Lumi = (window.Lumi = window.Lumi || {});
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /** Keeps Tab and Shift+Tab inside a container. */
  Lumi.trapFocus = function (container, event) {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- <site-header> ---------- */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.reel = this.querySelector('[data-reel-menu]');

      this.onWishlist = (event) => {
        const handles = (event && event.detail && event.detail.handles) || Lumi.storage.get('lumi:wishlist', []);
        this.setWishlistCount(Array.isArray(handles) ? handles.length : 0);
      };
      Lumi.on('lumi:wishlist:changed', this.onWishlist);
      this.onWishlist();

      this.onCartUpdated = (event) => {
        const detail = event.detail || {};
        if (detail.sections && detail.sections['cart-icon-bubble']) return; // swapped by Lumi.swapSections
        if (detail.cart) this.setCartCount(detail.cart.item_count);
      };
      Lumi.on('lumi:cart:updated', this.onCartUpdated);

      this.onClick = (event) => {
        const cart = event.target.closest('[data-cart-trigger]');
        if (!cart || event.metaKey || event.ctrlKey || event.shiftKey) return;
        if (Lumi.settings && Lumi.settings.cartType === 'drawer' && customElements.get('cart-drawer')) {
          event.preventDefault();
          Lumi.emit('lumi:cart:open');
        }
      };
      this.addEventListener('click', this.onClick);

      if (this.reel) {
        this.reelTriggers = this.querySelectorAll(`[popovertarget="${this.reel.id}"]`);
        this.reel.addEventListener('toggle', (event) => {
          const open = event.newState === 'open';
          this.reelTriggers.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
        });
        // Theme editor: show the reel while a category tile is selected.
        document.addEventListener('shopify:block:select', (event) => {
          if (this.reel.contains(event.target) && !this.reel.matches(':popover-open')) this.reel.showPopover();
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this.reel.contains(event.target) && this.reel.matches(':popover-open')) this.reel.hidePopover();
        });
      }
    }

    disconnectedCallback() {
      Lumi.off('lumi:wishlist:changed', this.onWishlist);
      Lumi.off('lumi:cart:updated', this.onCartUpdated);
    }

    setWishlistCount(count) {
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.querySelectorAll('[data-wishlist-link]').forEach((link) => {
        const label = count === 0 ? link.dataset.label : count === 1 ? link.dataset.labelOne : link.dataset.labelOther;
        if (label) link.setAttribute('aria-label', label.replace(/\{\{\s*count\s*\}\}/, count));
      });
    }

    setCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = !count;
      });
    }
  }

  /* ---------- <menu-drawer>: full-screen mobile menu ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-drawer-open]');
      this.dialog = this.querySelector('dialog');
      if (!this.button || !this.dialog) return;

      // JS owns the dialog from here; the command attributes are the no-JS fallback.
      this.querySelectorAll('[commandfor]').forEach((el) => {
        el.removeAttribute('commandfor');
        el.removeAttribute('command');
      });

      this.button.addEventListener('click', () => this.open());
      this.dialog.querySelectorAll('[data-drawer-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('keydown', (event) => Lumi.trapFocus(this.dialog, event));
      this.dialog.addEventListener('close', () => this.afterClose());
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.close(true);
      });

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = () => {
        if (this.desktop.matches && this.dialog.open) this.close(true);
      };
      this.desktop.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      if (this.desktop) this.desktop.removeEventListener('change', this.onBreakpoint);
    }

    open() {
      if (this.dialog.open) return;
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      // Swipe down to dismiss on touch screens (assets/app-shell.js).
      if (Lumi.app && Lumi.app.enableSheetSwipe) Lumi.app.enableSheetSwipe(this.dialog, { onClose: () => this.close() });
      this.button.setAttribute('aria-expanded', 'true');
      Lumi.lockScroll(true);
      const close = this.dialog.querySelector('[data-drawer-close]');
      if (close) close.focus();
      Lumi.emit('lumi:dialog:opened', { id: this.dialog.id });
    }

    close(immediate) {
      if (!this.dialog.open || this.closing) return;
      if (immediate || Lumi.reducedMotion()) {
        this.dialog.close();
        return;
      }
      this.closing = true;
      this.dialog.classList.add('is-closing');
      window.setTimeout(() => this.dialog.close(), 220);
    }

    afterClose() {
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      this.button.setAttribute('aria-expanded', 'false');
      Lumi.lockScroll(false);
      if (this.button.offsetParent !== null) this.button.focus();
      Lumi.emit('lumi:dialog:closed', { id: this.dialog.id });
    }
  }

  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);
})();
